"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "radix-ui"
import {
  ExternalLinkIcon,
  ScanIcon,
  XIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "lucide-react"

import { Button, buttonVariants } from "@workspace/ui/components/button"
import {
  MAX_ZOOM,
  MIN_ZOOM,
  anchoredScroll,
  clampZoom,
  fitSize,
  pointerDistance,
  stepZoom,
  toggleZoom,
  type Size,
} from "@workspace/ui/lib/image-zoom"
import type { ReadingDirection } from "@workspace/ui/lib/lightbox-navigation"
import { cn } from "@workspace/ui/lib/utils"

export interface ImageViewerLabels {
  close: string
  zoomIn: string
  zoomOut: string
  /** Back to the whole image on screen. */
  fit: string
  openInNewTab: string
}

/** Two taps closer than this (ms, px) are a double-tap. */
const DOUBLE_TAP_MS = 300
const TAP_SLOP_PX = 10
/**
 * Gap around the image, on each side. Inline px, not a spacing utility: a tenant
 * theme may rescale Tailwind's spacing, and the fit maths must match exactly.
 */
const VIEWPORT_PADDING_PX = 8

type Point = { x: number; y: number }

/**
 * Full-screen, zoomable viewer for one image — made for phone photos of a paper
 * menu, where the diner needs to read small print.
 *
 * Opens fitted to the screen. Zoom with the toolbar, `+`/`-`/`0`, a pinch on
 * touch screens, ctrl/⌘ + wheel or a trackpad pinch, or a double-click /
 * double-tap (toggles fit ↔ 2.5×); the point under the fingers or pointer stays
 * put. A zoomed image pans by dragging, scrolling or the arrow keys (the image
 * area is focusable). "Open in new tab" hands the raw image to the browser's
 * own viewer as a fallback.
 *
 * Radix Dialog: focus trapped inside, Esc closes, focus returns to the opener.
 * The image area is always laid out LTR so scroll offsets mean the same thing
 * in RTL; the toolbar follows `dir`. Colour comes from theme tokens only. Hook
 * for tenant CSS: `data-slot="image-viewer"`.
 */
function ImageViewer({
  open,
  onOpenChange,
  src,
  alt,
  title,
  labels,
  dir = "ltr",
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  src: string
  /** The image's alt text. */
  alt: string
  /** Names the dialog (shown in the toolbar). */
  title: string
  labels: ImageViewerLabels
  dir?: ReadingDirection
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/95 supports-backdrop-filter:backdrop-blur-sm data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0" />
        <DialogPrimitive.Content
          data-slot="image-viewer"
          dir={dir}
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col text-foreground outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0"
        >
          {/* Keyed by `src` so zoom and scroll start fresh for each image. */}
          <ZoomableImage
            key={src}
            src={src}
            alt={alt}
            title={title}
            labels={labels}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function ZoomableImage({
  src,
  alt,
  title,
  labels,
}: {
  src: string
  alt: string
  title: string
  labels: ImageViewerLabels
}) {
  const viewport = React.useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = React.useState(MIN_ZOOM)
  const [natural, setNatural] = React.useState<Size>({ width: 0, height: 0 })
  const [box, setBox] = React.useState<Size>({ width: 0, height: 0 })
  // Latest zoom for the native wheel listener, which is bound once.
  const zoomRef = React.useRef(zoom)
  // Set by `zoomTo`, consumed after layout to keep the zoom's focal point still.
  const anchor = React.useRef<{
    point: Point
    scroll: Point
    from: Size
  } | null>(null)
  const pointers = React.useRef(new Map<number, Point>())
  const pinch = React.useRef<{ distance: number; zoom: number } | null>(null)
  const pan = React.useRef<Point | null>(null)
  const press = React.useRef<{ at: Point; time: number } | null>(null)
  const lastTap = React.useRef<{ at: Point; time: number } | null>(null)

  const zoomTo = React.useCallback((next: number, client?: Point) => {
    const el = viewport.current
    const target = clampZoom(next)
    if (!el || target === zoomRef.current) return
    const rect = el.getBoundingClientRect()
    anchor.current = {
      point: client
        ? { x: client.x - rect.left, y: client.y - rect.top }
        : { x: el.clientWidth / 2, y: el.clientHeight / 2 },
      scroll: { x: el.scrollLeft, y: el.scrollTop },
      from: { width: el.scrollWidth, height: el.scrollHeight },
    }
    zoomRef.current = target
    setZoom(target)
  }, [])

  React.useLayoutEffect(() => {
    const el = viewport.current
    const pending = anchor.current
    anchor.current = null
    if (!el || !pending) return
    el.scrollLeft = anchoredScroll({
      scroll: pending.scroll.x,
      pointer: pending.point.x,
      fromExtent: pending.from.width,
      toExtent: el.scrollWidth,
      viewport: el.clientWidth,
    })
    el.scrollTop = anchoredScroll({
      scroll: pending.scroll.y,
      pointer: pending.point.y,
      fromExtent: pending.from.height,
      toExtent: el.scrollHeight,
      viewport: el.clientHeight,
    })
  }, [zoom])

  React.useEffect(() => {
    const el = viewport.current
    if (!el) return
    const observer = new ResizeObserver(() =>
      setBox({ width: el.clientWidth, height: el.clientHeight })
    )
    observer.observe(el)
    // ctrl + wheel is also what a trackpad pinch sends. Bound natively: React's
    // wheel listener is passive, so it could not stop the page zooming instead.
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      zoomTo(zoomRef.current * Math.exp(-event.deltaY * 0.005), {
        x: event.clientX,
        y: event.clientY,
      })
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      observer.disconnect()
      el.removeEventListener("wheel", onWheel)
    }
  }, [zoomTo])

  const fit = fitSize(natural, {
    width: box.width - VIEWPORT_PADDING_PX * 2,
    height: box.height - VIEWPORT_PADDING_PX * 2,
  })
  const sized = fit.width > 0

  const onTap = (at: Point, time: number) => {
    const last = lastTap.current
    if (
      last &&
      time - last.time < DOUBLE_TAP_MS &&
      pointerDistance(last.at, at) < TAP_SLOP_PX * 3
    ) {
      lastTap.current = null
      zoomTo(toggleZoom(zoomRef.current), at)
      return
    }
    lastTap.current = { at, time }
  }

  return (
    <>
      <div className="flex items-center gap-1 p-3">
        <DialogPrimitive.Title className="min-w-0 flex-1 truncate px-1 text-sm font-medium">
          {title}
        </DialogPrimitive.Title>
        <Button
          variant="ghost"
          size="icon"
          aria-label={labels.zoomOut}
          disabled={zoom <= MIN_ZOOM}
          onClick={() => zoomTo(stepZoom(zoom, -1))}
        >
          <ZoomOutIcon />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={labels.zoomIn}
          disabled={zoom >= MAX_ZOOM}
          onClick={() => zoomTo(stepZoom(zoom, 1))}
        >
          <ZoomInIcon />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={labels.fit}
          disabled={zoom <= MIN_ZOOM}
          onClick={() => zoomTo(MIN_ZOOM)}
        >
          <ScanIcon />
        </Button>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={labels.openInNewTab}
          className={buttonVariants({ variant: "ghost", size: "icon" })}
        >
          <ExternalLinkIcon className="rtl:-scale-x-100" />
        </a>
        <DialogPrimitive.Close asChild>
          <Button variant="ghost" size="icon" aria-label={labels.close}>
            <XIcon />
          </Button>
        </DialogPrimitive.Close>
      </div>

      <div
        ref={viewport}
        dir="ltr"
        tabIndex={0}
        role="group"
        aria-label={alt}
        onKeyDown={(event) => {
          if (event.key === "+" || event.key === "=") zoomTo(stepZoom(zoom, 1))
          else if (event.key === "-") zoomTo(stepZoom(zoom, -1))
          else if (event.key === "0") zoomTo(MIN_ZOOM)
          else return
          event.preventDefault()
        }}
        onPointerDown={(event) => {
          // Keeps a drag tracking past the edge. Throws for a pointer that is
          // already gone; the gesture still works without capture.
          try {
            event.currentTarget.setPointerCapture(event.pointerId)
          } catch {
            // Capture is a nicety, not a requirement.
          }
          const at = { x: event.clientX, y: event.clientY }
          pointers.current.set(event.pointerId, at)
          const [a, b] = [...pointers.current.values()]
          if (a && b) {
            pinch.current = { distance: pointerDistance(a, b), zoom }
            pan.current = null
            press.current = null
          } else {
            pan.current = at
            press.current = { at, time: event.timeStamp }
          }
        }}
        onPointerMove={(event) => {
          if (!pointers.current.has(event.pointerId)) return
          const at = { x: event.clientX, y: event.clientY }
          pointers.current.set(event.pointerId, at)
          const [a, b] = [...pointers.current.values()]
          if (pinch.current && a && b && pinch.current.distance > 0) {
            zoomTo(
              (pinch.current.zoom * pointerDistance(a, b)) /
                pinch.current.distance,
              { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
            )
          } else if (pan.current) {
            event.currentTarget.scrollLeft -= at.x - pan.current.x
            event.currentTarget.scrollTop -= at.y - pan.current.y
            pan.current = at
          }
        }}
        onPointerUp={(event) => {
          pointers.current.delete(event.pointerId)
          const start = press.current
          press.current = null
          const at = { x: event.clientX, y: event.clientY }
          if (
            start &&
            event.timeStamp - start.time < DOUBLE_TAP_MS &&
            pointerDistance(start.at, at) < TAP_SLOP_PX
          ) {
            onTap(at, event.timeStamp)
          }
          if (pointers.current.size < 2) pinch.current = null
          // Lifting one finger of a pinch carries on as a pan with the other.
          const [rest] = [...pointers.current.values()]
          pan.current = rest ?? null
        }}
        onPointerCancel={(event) => {
          pointers.current.delete(event.pointerId)
          pinch.current = null
          pan.current = null
          press.current = null
        }}
        className={cn(
          "min-h-0 flex-1 touch-none overflow-auto overscroll-contain outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset",
          zoom > MIN_ZOOM ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"
        )}
      >
        {/* `w-max` + `m-auto`: centred while it fits, scrollable to every edge
            once it is larger than the viewport. */}
        <div
          className="flex min-h-full w-max min-w-full"
          style={{ padding: VIEWPORT_PADDING_PX }}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            decoding="async"
            onLoad={(event) =>
              setNatural({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            style={
              sized
                ? {
                    width: fit.width * zoom,
                    height: fit.height * zoom,
                    maxWidth: "none",
                  }
                : undefined
            }
            className={cn(
              "m-auto rounded-[var(--radius)] bg-muted",
              !sized && "max-h-full max-w-full"
            )}
          />
        </div>
      </div>
    </>
  )
}

export { ImageViewer }
